// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { useState, useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Input,
  Form,
  Label,
  Spinner,
  Progress,
  Alert
} from "reactstrap";
import { useForm } from "react-hook-form";
import SuiteLayout from "../../_suite/SuiteLayout";
import AiStudioDashboard from "../../aiStudio";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_DEMO_CHART_SHELL_200,
  SUITE_PROGRESS_HEIGHT_6,
  AI_STUDIO_USAGE_BAR_WIDTH
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function AiStudioUsageBar({
  tokens,
  maxTokens
}: {
  tokens: number;
  maxTokens: number;
}) {
  const barStyle = useMemo(() => {
    const ratio = maxTokens > 0 ? tokens / maxTokens : 0;
    const h = Math.max(8, Math.round(ratio * 140));
    return {
      ...AI_STUDIO_USAGE_BAR_WIDTH,
      height: `${h}px`
    };
  }, [tokens, maxTokens]);
  return (
    <div
      className="bg-primary rounded-top mx-auto"
      style={barStyle}
      title={`${tokens} tokens`}
    />
  );
}

function UsageByModelPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.aiStudio.listLogs(),
    []
  );
  const byModel = {};
  (data || []).forEach(l => {
    byModel[l.model] = (byModel[l.model] || 0) + (l.tokens || 0);
  });
  const modelEntries = Object.entries(byModel);
  const maxTokens = modelEntries.reduce(
    (acc, [, v]) => Math.max(acc, Number(v) || 0),
    0
  );
  const maxForScale = maxTokens > 0 ? maxTokens : 1;
  return (
    <SuitePageShell heading={<IntlMessages id="suite.aistudio.logs" />}>
      <Card className="mb-4">
        <CardBody>
          <div
            className="bg-light rounded d-flex align-items-end gap-2 p-3"
            style={SUITE_DEMO_CHART_SHELL_200}
          >
            {modelEntries.map(([m, t]) => (
              <div key={m} className="text-center flex-grow-1">
                <AiStudioUsageBar
                  tokens={Number(t) || 0}
                  maxTokens={maxForScale}
                />
                <div className="small mt-1">{m}</div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
      <AsyncStateBoundary loading={loading} error={error}>
        <pre className="bg-dark text-success small p-3 rounded mb-0">
          {(data || [])
            .map(
              l =>
                `${l.ts} ${l.model} ${l.tokens} tok${l.costUsd != null ? ` $${l.costUsd}` : ""}\n`
            )
            .join("")}
        </pre>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ApiKeysVaultPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.aiStudio.listKeys(),
    []
  );
  const [revealed, setRevealed] = useState({});
  return (
    <SuitePageShell heading={<IntlMessages id="suite.aistudio.keys" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(k => (
          <Card key={k.id} className="mb-3 border-0 shadow-sm font-monospace">
            <CardBody className="d-flex justify-content-between flex-wrap align-items-center">
              <div>
                <strong>{k.label}</strong>
                <div className="small text-muted">{k.created}</div>
              </div>
              <div>
                {revealed[k.id] ? (
                  <span>{k.prefix}</span>
                ) : (
                  <span>••••••••••••</span>
                )}
                <Button
                  size="sm"
                  className="ms-2"
                  onClick={() =>
                    setRevealed(r => ({ ...r, [k.id]: !r[k.id] }))
                  }
                >
                  {revealed[k.id] ? "Hide" : "Reveal"}
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ExperimentsComparePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.aiStudio.listExperiments(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.aistudio.experiments" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(e => (
            <Col md="6" key={e.id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <CardTitle tag="h5">{e.name}</CardTitle>
                  <Row>
                    <Col xs="6">
                      <div className="border rounded p-3 text-center">
                        <div className="small text-muted">Control</div>
                        <div className="h4">A</div>
                      </div>
                    </Col>
                    <Col xs="6">
                      <div className="border border-primary rounded p-3 text-center">
                        <div className="small text-muted">Variant</div>
                        <div className="h4">B</div>
                        <Badge>{e.trafficPct}% traffic</Badge>
                      </div>
                    </Col>
                  </Row>
                  <Badge className="mt-2">{e.status}</Badge>
                  {e.liftPct != null && (
                    <p className="small text-muted mt-2 mb-0">
                      Lift vs control: {e.liftPct > 0 ? "+" : ""}
                      {e.liftPct}%
                    </p>
                  )}
                  {e.winner && (
                    <Alert color="success" className="mt-2 py-2 small mb-0">
                      Winner: <strong>{e.winner}</strong>
                    </Alert>
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function AiSettingsRatePage() {
  const { data, loading, reload } = useAsyncResource(
    () => suiteApi.aiStudio.getAiSettings(),
    []
  );
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();
  React.useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);
  return (
    <SuitePageShell heading={<IntlMessages id="suite.aistudio.settings" />}>
      {loading && !data && <Spinner />}
      {data && (
        <Row>
          <Col md="6">
            <Form
              onSubmit={handleSubmit(async v => {
                await suiteApi.aiStudio.saveAiSettings(v);
                reload();
              })}
            >
              <div className="mb-3">
                <Label>
                  <IntlMessages id="suite.aistudio.rpm-limit" />
                </Label>
                <Input type="text" {...register("rpmLimit")} />
              </div>
              <div className="mb-3">
                <Label>
                  <IntlMessages id="suite.aistudio.monthly-budget" />
                </Label>
                <Input type="text" {...register("monthlyBudgetUsd")} />
              </div>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                Save
              </Button>
            </Form>
          </Col>
          <Col md="6">
            <Card className="bg-primary text-white">
              <CardBody>
                <CardTitle className="text-white">Rate preview</CardTitle>
                <p className="small mb-0">
                  Requests would be throttled at the edge based on rpmLimit.
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </SuitePageShell>
  );
}

function ProjectDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.aiStudio.getProject(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.aistudio.project-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h3>{data.name}</h3>
              <p>
                {data.env} · Budget ${data.budgetUsd} · Used ${data.usedUsd}
              </p>
              {data.budgetUsd > 0 && (
                <>
                  <div className="small text-muted mb-1">Budget burn</div>
                  <Progress
                    value={Math.min(
                      100,
                      ((data.usedUsd || 0) / data.budgetUsd) * 100
                    )}
                    color={
                      data.usedUsd / data.budgetUsd > 0.85
                        ? "danger"
                        : "primary"
                    }
                  />
                </>
              )}
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function ProjectsListPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.aiStudio.listProjects(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.aistudio.projects" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(p => (
            <Col md="6" key={p.id} className="mb-3">
              <Card className="border-0 shadow-sm">
                <CardBody className="d-flex justify-content-between">
                  <div className="flex-grow-1 me-2">
                    <h5>{p.name}</h5>
                    <Badge>{p.env}</Badge>
                    {p.budgetUsd > 0 && (
                      <div className="mt-2">
                        <div className="small text-muted">Burn</div>
                        <Progress
                          value={Math.min(
                            100,
                            ((p.usedUsd || 0) / p.budgetUsd) * 100
                          )}
                          style={SUITE_PROGRESS_HEIGHT_6}
                        />
                      </div>
                    )}
                  </div>
                  <Button tag={Link} color="primary" size="sm" to={p.id} relative="path">
                    Open
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function AiStudioSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="ai-studio" />}>
        <Route index element={<AiStudioDashboard />} />
        <Route path="projects" element={<ProjectsListPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="keys" element={<ApiKeysVaultPage />} />
        <Route path="logs" element={<UsageByModelPage />} />
        <Route path="experiments" element={<ExperimentsComparePage />} />
        <Route path="settings" element={<AiSettingsRatePage />} />
      </Route>
    </Routes>
  );
}
